import React from 'react';
import './OverviewKpis.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsOverviewResponse } from '@brand24-original/llm-types';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { percentOf } from '../../helpers/overviewInsightHelpers';

interface OverviewKpisProps {
  overview: InsightsOverviewResponse | null;
  isLoading: boolean;
  isError?: boolean;
  /** Display name of the analyzed brand — the "no domain" tooltip names it. */
  brandLabel: string;
  /** Own brand → the fix is the project website; a competitor → a domain on the Keywords page. */
  isOwnBrand: boolean;
}

interface KpiCard {
  key: string;
  label: string;
  value: string;
  subtitle: string;
  icon: string;
  accent?: 'blue' | 'green';
  // Karta bez danych o domenach marki — pokazuje "—" z wyjaśnieniem zamiast mylącego zera.
  noDomainData?: boolean;
}

const OverviewKpis: React.FC<OverviewKpisProps> = ({
  overview,
  isLoading,
  isError,
  brandLabel,
  isOwnBrand,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="stats__panel overview-kpis">
        <div className="overview-kpis__grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="overview-kpis__card overview-kpis__card--skeleton" />
          ))}
        </div>
      </div>
    );
  }

  // Błąd API to nie jest zmierzony brak danych — "No data" sugerowałby, że projekt nic nie zebrał.
  if (isError) {
    return (
      <div className="stats__panel overview-kpis">
        <EmptyPanel textKey="somethingWentWrong" />
      </div>
    );
  }

  if (!overview || overview.totalResponses === 0) {
    return (
      <div className="stats__panel overview-kpis">
        <EmptyPanel />
      </div>
    );
  }

  const { totalResponses, visibleResponses, citedResponses, visibleAndCitedResponses } = overview;
  const visibilityPct = percentOf(visibleResponses, totalResponses);
  const hasCited = citedResponses !== null && visibleAndCitedResponses !== null;
  const citedPct = hasCited ? percentOf(citedResponses, totalResponses) : null;
  const visibleAndCitedPct = hasCited ? percentOf(visibleAndCitedResponses, totalResponses) : null;

  const cards: KpiCard[] = [
    {
      key: 'visibility',
      label: t('insightPage.overview.brandVisibility'),
      value: `${visibilityPct}%`,
      subtitle: t('insightPage.overview.responsesOfTotal', {
        value: visibleResponses,
        total: totalResponses,
      }),
      icon: 'ico--megaphone',
      accent: 'blue',
    },
    {
      key: 'cited',
      label: t('insightPage.overview.citedAsSource'),
      value: hasCited ? `${citedPct}%` : '—',
      subtitle: hasCited
        ? t('insightPage.overview.responsesOfTotal', {
            value: citedResponses,
            total: totalResponses,
          })
        : '',
      icon: 'ico--external-link',
      accent: 'green',
      noDomainData: !hasCited,
    },
    {
      key: 'visibilityPlusCited',
      label: t('insightPage.overview.visibilityPlusCited'),
      value: hasCited ? `${visibleAndCitedPct}%` : '—',
      subtitle: hasCited ? t('insightPage.overview.mentionBackedByOwnSource') : '',
      icon: 'ico--circle-check',
      noDomainData: !hasCited,
    },
    {
      key: 'gap',
      label: t('insightPage.overview.citationGap'),
      value: hasCited ? `${visibilityPct - (citedPct as number)} pp` : '—',
      subtitle: hasCited ? t('insightPage.overview.visibilityMinusCited') : '',
      icon: 'ico--trending-down',
      accent: 'blue',
      noDomainData: !hasCited,
    },
  ];

  return (
    <div className="stats__panel overview-kpis">
      <div className="overview-kpis__grid">
        {cards.map((card) => (
          <div
            key={card.key}
            className={['overview-kpis__card', card.accent ? `overview-kpis__card--${card.accent}` : '']
              .filter(Boolean)
              .join(' ')}
          >
            <div className="overview-kpis__label">
              <span className={`overview-kpis__icon ico--xs ${card.icon}`} />
              {card.label}
            </div>
            <div className="overview-kpis__value">
              {card.value}
              {card.noDomainData && (
                <CustomTooltip
                  tooltipText={
                    isOwnBrand ? 'pageTrafficNoDomainDescription' : 'insightPage.overview.noDomainForBrand'
                  }
                  options={{ brand: brandLabel }}
                  addMargin={false}
                />
              )}
            </div>
            <div className="overview-kpis__subtitle">{card.subtitle}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OverviewKpis;
